import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, ChevronRight } from 'lucide-react';
import { CHANGELOG } from '@/data/changelog';

const LS_KEY = 'cratelab_seen_changelog';

const readSeen = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const markSeen = (key) => {
  try {
    const seen = new Set(readSeen());
    seen.add(key);
    localStorage.setItem(LS_KEY, JSON.stringify([...seen]));
  } catch {
    /* localStorage disabled — silent */
  }
};

/**
 * Auto-opening "What's New" modal.
 *
 * On mount we look at the most recent entry in CHANGELOG. If its `key` has
 * not yet been acknowledged in localStorage, the modal opens after a small
 * delay (so it doesn't fight the SW update toast or the page entrance
 * animations). Closing it stores the key so it never auto-opens again for
 * that build.
 *
 * If `manualOpen` is provided (paired with `onClose`), the modal can also
 * be controlled externally — handy for a "What's new" link in Settings.
 */
export default function WhatsNewModal({ manualOpen, onClose }) {
  const [open, setOpen] = useState(false);
  const latest = CHANGELOG[0];

  useEffect(() => {
    if (manualOpen) {
      setOpen(true);
      return;
    }
    if (!latest) return;
    // Only auto-open the modal on the authenticated dashboard so it can't
    // intercept clicks on the home-page LoginPopover, /profile Save buttons,
    // or any other interactive surface on deep pages.
    const path = typeof window !== 'undefined' ? window.location.pathname : '/';
    const allowed = path === '/dashboard';
    if (!allowed) return;
    const seen = readSeen();
    if (seen.includes(latest.key)) return;
    // Wait a beat so the user lands on the page first.
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, [latest, manualOpen]);

  const handleClose = () => {
    setOpen(false);
    if (latest) markSeen(latest.key);
    if (onClose) onClose();
  };

  if (!latest) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="whats-new-modal"
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-white/80 backdrop-blur-sm"
            onClick={handleClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Card */}
          <motion.div
            className="relative w-full max-w-lg bg-white border border-[#E2E8F0] shadow-[0_0_40px_rgba(37,99,255,0.12)]"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ type: 'spring', damping: 22, stiffness: 240 }}
          >
            {/* Header band */}
            <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 border-b border-[#E2E8F0]">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-9 h-9 flex-shrink-0 bg-[#DBEAFE] border border-[#E2E8F0] flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-[#2563FF]" />
                </div>
                <div className="min-w-0">
                  <p className="font-mono text-[10px] tracking-[0.2em] text-[#2563FF] uppercase">
                    What's new · v{latest.version}
                  </p>
                  <h2 className="font-heading text-xl text-[#0F172A] leading-tight mt-1">
                    {latest.tagline || 'Latest improvements'}
                  </h2>
                  <p className="font-mono text-[10px] text-[#64748B] mt-1">
                    Released {latest.date}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                aria-label="Close"
                className="p-1 text-[#64748B] hover:text-[#0F172A] transition-colors"
                data-testid="whats-new-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Highlights */}
            <ul className="px-6 py-5 space-y-3" data-testid="whats-new-highlights">
              {latest.highlights.map((line, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.04 }}
                  className="flex items-start gap-2.5 font-body text-sm text-[#0F172A] leading-relaxed"
                >
                  <ChevronRight className="w-4 h-4 mt-0.5 text-[#2563FF] flex-shrink-0" />
                  <span>{line}</span>
                </motion.li>
              ))}
            </ul>

            {/* Footer */}
            <div className="px-6 pb-5 pt-2 flex items-center justify-end gap-3 border-t border-[#E2E8F0]">
              <button
                onClick={handleClose}
                className="btn-primary inline-flex items-center gap-2 px-5 py-2 text-sm"
                data-testid="whats-new-dismiss-btn"
              >
                Got it
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
